import './video-info.component.scss';
import { buttonize } from '../../../../utils/a11y.utils';

import { h } from 'preact';
import { useMemo, useState } from 'preact/hooks';

import { IconShare } from '../../../../common/icons/icon-share';
import { useTranslation } from '../../../../i18n';
import type { Video } from '../../../models/video.interface';

interface VideoInfoProps {
  video: Video;
  showTitle: boolean;
  showArtist: boolean;
  showDescription: boolean;
}

export function VideoInfo({ showTitle, showArtist, showDescription, video }: VideoInfoProps) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  // Parse once, derive the collapsed HTML and whether we need a toggle
  const { firstTwoHtml, hasMoreThanTwo } = useMemo(() => {
    const container = document.createElement('div');
    container.innerHTML = video.description;

    const paras = Array.from(container.querySelectorAll('p'));
    const hasMore = paras.length > 2;

    let collapsed = video.description; // fallback if we can't find two <p>
    if (paras.length >= 2) {
      collapsed = paras[0].outerHTML + paras[1].outerHTML;
    }

    return {
      firstTwoHtml: collapsed,
      hasMoreThanTwo: hasMore,
    };
  }, [video.description]);

  const isMobileDevice = (): boolean => {
    return /Mobi|Android|iPhone|iPad|iPod/.test(navigator.userAgent);
  };

  const showTooltip = (): void => {
    const tooltip = document.getElementById(`single-tooltip-${video.id}`);
    tooltip?.classList.add('show');

    // Hide the tooltip after 2 seconds
    setTimeout(() => {
      tooltip?.classList.remove('show');
    }, 2000);
  };

  const share = ($event: PointerEvent | MouseEvent): void => {
    $event.preventDefault();

    const url = window.location.href;
    if (navigator.share && isMobileDevice()) {
      navigator
        .share({
          title: video.name,
          url,
        })
        .then(() => console.log('Successfully shared'))
        .catch((error) => console.log('Error sharing:', error));
    } else if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => showTooltip())
        .catch((err) => console.log('Failed to copy: ', err));
    } else {
      alert(t('common.browserShareError'));
    }
  };

  return (
    <div class="single-video-info page-width">
      <div class="single-video-header">
        <div class="single-go-back" {...buttonize(() => window.history.back())}>
          <span>{t('auth.back')}</span>
        </div>
        <div class="single-video-share" {...buttonize(share)}>
          <IconShare width={15} height={15}></IconShare>
          <span>{t('common.share')}</span>
          <span id={`single-tooltip-${video.id}`} class="single-share-tooltip">
            {t('common.copied')}
          </span>
        </div>
      </div>
      {showTitle && (
        <div class="single-video-title-wrapper">
          <div class="single-video-title h3" title={video.name}>
            {video.name}
          </div>
        </div>
      )}

      {showArtist && !!video.artistName && (
        <div class="single-video-artist">{video.artistName}</div>
      )}

      {showDescription && video?.description && (
        <>
          <div class="single-video-description-wrapper">
            <div
              class={`single-video-description ${expanded ? 'expanded' : 'collapsed'}`}
              dangerouslySetInnerHTML={{ __html: expanded ? video.description : firstTwoHtml }}
            />
          </div>

          {hasMoreThanTwo && (
            <p
              class="single-video-toggle-description"
              {...buttonize(() => setExpanded((v) => !v))}
              aria-expanded={expanded}
            >
              {expanded ? t('common.readLess') : t('common.readMore')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
